import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoice,
  faFileInvoiceDollar,
  faSpinner,
  faExclamationTriangle,
  faCheckCircle,
  faHourglass,
  faClock,
  faTasks,
  faSearch,
  faHistory,
  faRotateRight,
  faLink,
  faLinkSlash
} from '@fortawesome/free-solid-svg-icons';
import { formatDistanceToNow } from 'date-fns';
import { useAuth } from '../../../context/AuthContext';
import MainLayout from '../../Layout/MainLayout';

// Export component definition for dynamic loading
export const componentDefinition = {
  "template_type": "custom",
  "metadata": {
    "displayName": "Bill Info",
    "description": "View all processed bills with vendor and NetSuite details",
    "icon": "file-invoice"
  },
  "config": {
    "renderMode": "full-page"
  }
};

const BillProcessingHistory = () => {
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [poFilter, setPoFilter] = useState('all'); // 'all' | 'linked' | 'no_po' — admin only

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/bill-processing/history', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch bill processing history');
      }

      const data = await response.json();
      setHistory(data.history || []);
    } catch (err) {
      console.error('Error fetching bill history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'completed': {
        icon: faCheckCircle,
        className: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
        label: 'Completed'
      },
      'running': {
        icon: faHourglass,
        className: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
        label: 'Running'
      },
      'awaiting_user': {
        icon: faClock,
        className: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200',
        label: 'Awaiting User'
      },
      'failed': {
        icon: faExclamationTriangle,
        className: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
        label: 'Failed'
      },
      'paused': {
        icon: faClock,
        className: 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200',
        label: 'Paused'
      }
    };

    const config = statusConfig[status] || statusConfig['paused'];

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.className}`}>
        <FontAwesomeIcon icon={config.icon} className="mr-1" />
        {config.label}
      </span>
    );
  };

  // PO linkage badge. Rendered for admins only.
  const getPoBadge = (isLinked) => (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
      isLinked
        ? 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200'
        : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300'
    }`}>
      <FontAwesomeIcon icon={isLinked ? faLink : faLinkSlash} className="mr-1" />
      {isLinked ? 'PO Linked' : 'No PO'}
    </span>
  );

  // Gradient tile per status (icon container on each card)
  const getCardIconGradient = (status) => {
    if (status === 'completed') return 'bg-gradient-to-br from-green-500 to-emerald-600';
    if (status === 'awaiting_user') return 'bg-gradient-to-br from-yellow-500 to-orange-500';
    if (status === 'failed') return 'bg-gradient-to-br from-red-500 to-rose-600';
    return 'bg-gradient-to-br from-purple-500 to-indigo-600';
  };

  // Relative time (e.g. "2 hours ago"); falls back to absolute if the date is unparseable.
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch (e) {
      try {
        return new Intl.DateTimeFormat('en-US', {
          year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
        }).format(new Date(dateString));
      } catch (e2) {
        return 'N/A';
      }
    }
  };

  const formatStepName = (stepName) => {
    if (!stepName || stepName === 'N/A') return 'N/A';
    return stepName.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  const pendingCount = history.filter(i => i.status === 'awaiting_user').length;
  const completedCount = history.filter(i => i.status === 'completed').length;
  const totalCount = history.length;

  const filteredHistory = history.filter(item => {
    if (filter !== 'all' && item.status !== filter) return false;
    if (isAdmin && poFilter !== 'all') {
      const linked = item.po_linked === true;
      if (poFilter === 'linked' && !linked) return false;
      if (poFilter === 'no_po' && linked) return false;
    }
    if (searchTerm) {
      const hay = `${item.vendor_identified || ''} ${item.bill_name || ''}`.toLowerCase();
      if (!hay.includes(searchTerm.toLowerCase())) return false;
    }
    return true;
  });

  const tabs = [
    { key: 'all', label: 'All', icon: faHistory, activeText: 'text-purple-600 dark:text-purple-400' },
    { key: 'awaiting_user', label: 'Awaiting User', icon: faClock, activeText: 'text-yellow-600 dark:text-yellow-400', count: pendingCount },
    { key: 'completed', label: 'Completed', icon: faCheckCircle, activeText: 'text-green-600 dark:text-green-400', count: completedCount },
    { key: 'failed', label: 'Failed', icon: faExclamationTriangle, activeText: 'text-red-600 dark:text-red-400' }
  ];

  if (loading) {
    return (
      <MainLayout title="Bill Info">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <FontAwesomeIcon icon={faSpinner} className="text-4xl text-purple-500 animate-spin mb-4" />
            <p className="text-gray-600 dark:text-gray-400">Loading bill processing history...</p>
          </div>
        </div>
      </MainLayout>
    );
  }

  if (error) {
    return (
      <MainLayout title="Bill Info">
        <div className="flex items-center justify-center min-h-96">
          <div className="text-center">
            <FontAwesomeIcon icon={faExclamationTriangle} className="text-4xl text-red-500 mb-4" />
            <p className="text-red-600 dark:text-red-400 mb-4">Error: {error}</p>
            <button onClick={fetchHistory} className="btn btn-primary">Try Again</button>
          </div>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout title="Bill Info">
      <div className="max-w-6xl mx-auto p-6 space-y-6">

        {/* Header Section */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-purple-500 via-indigo-500 to-blue-600 rounded-3xl mb-6 shadow-xl">
            <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-white text-2xl" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600 bg-clip-text text-transparent mb-4">
            Bill Processing History
          </h1>
          <p className="text-gray-600 dark:text-gray-400 text-lg sm:text-xl max-w-2xl mx-auto">
            All processed bills with vendor and NetSuite details
          </p>
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-2">
          <div className="flex flex-wrap gap-1 bg-gray-100 dark:bg-gray-800 rounded-xl p-1">
            {tabs.map(tab => (
              <button
                key={tab.key}
                onClick={() => setFilter(tab.key)}
                className={`flex items-center space-x-2 px-4 sm:px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                  filter === tab.key
                    ? `bg-white dark:bg-gray-700 ${tab.activeText} shadow-sm`
                    : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                }`}
              >
                <FontAwesomeIcon icon={tab.icon} className="text-sm" />
                <span>{tab.label}</span>
                {typeof tab.count === 'number' && (
                  <span className="ml-1 px-2 py-0.5 bg-gray-200 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs rounded-full">
                    {tab.count}
                  </span>
                )}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 w-full sm:w-auto">
            {isAdmin && (
              <select
                value={poFilter}
                onChange={(e) => setPoFilter(e.target.value)}
                title="Filter by PO linkage"
                className="flex-shrink-0 px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
              >
                <option value="all">PO: All</option>
                <option value="linked">PO Linked</option>
                <option value="no_po">No PO</option>
              </select>
            )}
            <div className="relative w-full sm:w-72">
              <FontAwesomeIcon icon={faSearch} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by vendor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-3 border border-gray-300 dark:border-gray-600 rounded-xl focus:ring-2 focus:ring-purple-500 dark:bg-gray-800 dark:text-white transition-all duration-200"
              />
            </div>
            <button
              onClick={fetchHistory}
              title="Refresh"
              className="flex-shrink-0 p-3 rounded-xl border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              <FontAwesomeIcon icon={faRotateRight} />
            </button>
          </div>
        </div>

        {/* Cards */}
        {filteredHistory.length === 0 ? (
          <div className="text-center py-16">
            <FontAwesomeIcon icon={faFileInvoice} className="text-6xl text-gray-300 dark:text-gray-600 mb-6" />
            <h3 className="text-2xl font-semibold text-gray-500 dark:text-gray-400 mb-3">
              {searchTerm ? 'No matches found' : 'No bills found'}
            </h3>
            <p className="text-gray-400 dark:text-gray-500">
              {searchTerm
                ? 'Try adjusting your search terms'
                : (isAdmin && poFilter !== 'all')
                  ? `No ${poFilter === 'linked' ? 'PO Linked' : 'No PO'} bills${filter === 'all' ? '' : ` in ${formatStepName(filter)}`}`
                  : (filter === 'all' ? 'Start processing bills to see them here' : `No ${formatStepName(filter)} bills`)}
            </p>
          </div>
        ) : (
          <div className="grid gap-6">
            {filteredHistory.map((item, index) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.04, 0.4) }}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-lg border border-gray-100 dark:border-gray-700 hover:shadow-xl hover:border-purple-200 dark:hover:border-purple-600 transition-all duration-300 overflow-hidden"
              >
                <div className="p-6 sm:p-8">
                  <div className="flex items-start justify-between gap-6">
                    {/* Left: content */}
                    <div className="flex items-start space-x-4 sm:space-x-6 flex-1 min-w-0">
                      <div className={`flex-shrink-0 w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center shadow-lg ${getCardIconGradient(item.status)}`}>
                        <FontAwesomeIcon icon={faFileInvoiceDollar} className="text-white text-lg" />
                      </div>

                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-3 mb-1">
                          <h3 className="text-lg font-semibold text-gray-900 dark:text-white truncate">
                            {item.vendor_identified || item.bill_name || 'Unknown Vendor'}
                          </h3>
                          {getStatusBadge(item.status)}
                          {isAdmin && getPoBadge(item.po_linked === true)}
                        </div>

                        {item.bill_name && (
                          <p className="text-sm text-gray-600 dark:text-gray-300 truncate mb-2">
                            <FontAwesomeIcon icon={faFileInvoice} className="mr-2 text-gray-400" />
                            {item.bill_name}
                          </p>
                        )}

                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-gray-500 dark:text-gray-400">
                          <span className="flex items-center">
                            <FontAwesomeIcon icon={faClock} className="mr-1" />
                            {formatDate(item.started_at)}
                          </span>
                          {item.current_step && item.current_step !== 'N/A' && (
                            <span className="flex items-center">
                              <FontAwesomeIcon icon={faTasks} className="mr-1" />
                              {formatStepName(item.current_step)}
                            </span>
                          )}
                          {item.run_id && (
                            <span className="text-gray-400 dark:text-gray-500">
                              Run {item.run_id.substring(0, 8)}…
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right: NetSuite link */}
                    <div className="flex-shrink-0">
                      {item.netsuite_bill_url ? (
                        <a
                          href={item.netsuite_bill_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Open in NetSuite"
                          className="inline-flex items-center space-x-2 px-4 sm:px-5 py-2.5 rounded-xl font-semibold text-white bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-lg hover:shadow-xl transition-all duration-200"
                        >
                          <span className="hidden sm:inline">Bill {item.netsuite_bill_id}</span>
                          <span className="sm:hidden">{item.netsuite_bill_id}</span>
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                          </svg>
                        </a>
                      ) : (item.netsuite_bill_id && item.netsuite_bill_id !== 'N/A') ? (
                        <span className="inline-flex items-center px-4 py-2.5 rounded-xl text-sm font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200">
                          Bill {item.netsuite_bill_id}
                        </span>
                      ) : (
                        <span className="text-sm text-gray-400 dark:text-gray-500">No bill yet</span>
                      )}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {/* Footer stats */}
        <div className="mt-10 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold text-yellow-600 dark:text-yellow-400">{pendingCount}</div>
              <div className="text-gray-600 dark:text-gray-400">Awaiting User</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-green-600 dark:text-green-400">{completedCount}</div>
              <div className="text-gray-600 dark:text-gray-400">Completed</div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold text-purple-600 dark:text-purple-400">{totalCount}</div>
              <div className="text-gray-600 dark:text-gray-400">Total Bills</div>
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default BillProcessingHistory;
